import type { Permission } from '@fieldvision/contracts';
import {
  Activity,
  Boxes,
  ChartLine,
  Download,
  Network,
  FileSpreadsheet,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  ListTree,
  type LucideIcon,
  MapPin,
  Package,
  Route,
  ShieldAlert,
  SlidersHorizontal,
  Store,
  Swords,
  Tags,
  Target,
  Users,
} from 'lucide-react';

/**
 * The navigation model.
 *
 * Data rather than JSX so the permission gate is one filter over one list
 * instead of a conditional per link. Every item names the permission the server
 * actually enforces on the endpoints behind it — `any` means the item appears if
 * the user holds at least one, which is right for screens that degrade (the
 * reports screen still shows the tabs you are allowed to see).
 *
 * This decides what to OFFER. It is not access control: a hidden item is not a
 * blocked one (TSP 2.2.2), and every route below is guarded server-side.
 */
export interface NavItem {
  to: string;
  label: string;
  any: Permission[];
  /**
   * The icon rides with the route rather than being looked up by path in the
   * shell. A new entry cannot then be added without one, which is what keeps
   * the sidebar from developing gaps.
   *
   * Icons here are an aid to re-finding a known item, never the only label —
   * every one renders beside its text (design-guidelines §8.4).
   */
  icon: LucideIcon;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    label: 'Pantauan',
    items: [
      { to: '/overview', label: 'Ringkasan', any: ['dashboard:read'], icon: LayoutDashboard },
      { to: '/reports', label: 'Laporan', any: ['report:read', 'dashboard:read'], icon: FileSpreadsheet },
      // One entry, not two: the paired report absorbed the separate competition
      // index. A screen with rupiah and no rival beside a screen with a rival and
      // no rupiah was a split nobody could hold in their head.
      { to: '/price-index', label: 'Indeks Harga', any: ['report:read'], icon: ChartLine },
      // The same metric one grain finer — one row per store visit rather than
      // per benchmark. Its own item rather than a tab, because the two row sets
      // do not overlap: that page lists the catalogue, this one lists
      // measurements. See `PriceVisitsPage`.
      { to: '/price-index/visits', label: 'Indeks Harga Detail', any: ['report:read'], icon: ListTree },
      /*
       * The SKU-grain planogram report. It sits BESIDE the outlet-grain view in
       * Laporan rather than absorbing it, unlike the price index above: "which
       * store is failing" and "which product is mis-shelved" are two jobs, and
       * the tab in Laporan is the one a supervisor working a route opens.
       */
      { to: '/planogram', label: 'Kepatuhan Planogram', any: ['report:read'], icon: LayoutGrid },
      /*
       * The same metric one grain finer — one row per store visit rather than
       * per SKU. Its own item rather than a tab, on the reasoning `Indeks Harga
       * Detail` above sets out: the two row sets do not overlap, because that
       * page lists the catalogue and this one lists measurements.
       */
      { to: '/planogram/visits', label: 'Planogram Detail', any: ['report:read'], icon: ListTree },
      { to: '/fraud', label: 'Kunjungan Terflag', any: ['fraud:read'], icon: ShieldAlert },
      /*
       * THE ONE PLACE A COMPLETE EXPORT IS MADE — every report above with the
       * rincian and photographs behind each row, as a ZIP built by the worker.
       * Last in the group because it is where you go AFTER choosing what to look
       * at, and `report:export` alone because every one of its endpoints is that
       * permission: a reader who cannot export has nothing to queue.
       */
      { to: '/downloads', label: 'Unduhan', any: ['report:export'], icon: Download },
    ],
  },
  {
    label: 'Data Induk',
    items: [
      { to: '/master/outlets', label: 'Outlet', any: ['outlet:read'], icon: Store },
      // Directly under Outlet, and in parent-then-child order, because that is the
      // relationship: a classification exists inside a channel and the two
      // screens are read together. `outlet:read` rather than a permission of
      // their own — see the controller for why they reuse the outlet's.
      { to: '/master/channels', label: 'Channel', any: ['outlet:read'], icon: Network },
      { to: '/master/classifications', label: 'Klasifikasi', any: ['outlet:read'], icon: Route },
      { to: '/master/regions', label: 'Distributor', any: ['outlet:read'], icon: MapPin },
      { to: '/master/skus', label: 'SKU', any: ['sku:read'], icon: Package },
      // Directly under SKU, because it is the axis that classifies it — and
      // `Layers` rather than `Tags`, which already means "competitor".
      { to: '/master/sku-categories', label: 'Kategori SKU', any: ['sku:read'], icon: Layers },
      // The SECOND axis (0054), beside Kategori rather than under it: the two
      // cross rather than nest, and an indented entry would say otherwise.
      { to: '/master/sku-groupings', label: 'Grouping SKU', any: ['sku:read'], icon: Boxes },
      { to: '/master/competitors', label: 'Kompetitor', any: ['sku:read'], icon: Tags },
      // The Plan half of FR-14. Master data by every test that matters: an admin
      // maintains it, it has a natural key, and nothing deletes it.
      { to: '/master/visit-targets', label: 'Target Kunjungan', any: ['target:read'], icon: Target },
      // The other Plan half of FR-14, and the same permission: a benchmark
      // against a rival is a target, maintained by the same admin.
      { to: '/master/competition-plans', label: 'Price Index', any: ['target:read'], icon: Swords },
      /*
       * "Impor Massal" was here, pointing at `/master/import`.
       *
       * It is off the nav because every entity it offered now carries its own
       * Impor and Ekspor buttons, on the screen whose data they change. The one
       * screen served six masters through a dropdown, which meant choosing the
       * entity twice — once by navigating, once in the picker — and reading an
       * all-or-nothing error table with no table beside it to check the result
       * against.
       *
       * THE ROUTE STILL RESOLVES. `/master/import` is reachable by URL and the
       * page still works, so a bookmark does not 404 while the change beds in.
       * Deleting it is a three-line change here, in `ConsoleApp.tsx` and in the
       * page file itself — everything it renders is already shared with
       * `EntityImportDialog`, which is what made this removal a deletion rather
       * than a migration.
       */
    ],
  },
  {
    label: 'Administrasi',
    items: [
      { to: '/users', label: 'Pengguna', any: ['user:read'], icon: Users },
      { to: '/users/roles', label: 'Peran & Izin', any: ['user:read'], icon: KeyRound },
      /*
       * `target:read`, matching Price Index rather than the two entries
       * above it. The only value here grades competition plans, and the same
       * administrator maintains both — gating it on `user:read` would tie
       * "may move a price band" to "may administer people", which is the wrong
       * pairing and would hide the screen from the person who needs it.
       */
      { to: '/settings', label: 'Pengaturan', any: ['target:read'], icon: SlidersHorizontal },
      /*
       * INF-04's console layer. Last in the group because it is the entry
       * nobody opens on an ordinary day — it is where you go after the pager
       * has already gone off, not a screen you browse.
       *
       * `system:read`, which is ADMIN-only and exists precisely so this is not
       * gated on `user:read` like the two entries above: seeing whether the
       * photo pipeline is alive is not the same job as administering people.
       */
      { to: '/system', label: 'Status Sistem', any: ['system:read'], icon: Activity },
    ],
  },
];

/** Every route this console serves, in nav order — used to pick a landing page. */
export const NAV_ITEMS: NavItem[] = NAV.flatMap((group) => group.items);

/**
 * The permissions that make someone a console user at all.
 *
 * Deliberately NOT "holds any permission a nav item asks for". SALES legitimately
 * holds `outlet:read` and `sku:read` — the field app needs both — so that test
 * lets a salesperson in to browse a read-only master-data
 * screen. Their rows are correctly scoped and every action is hidden, so nothing
 * leaks; it is simply not an application they have any business in, and landing
 * them there is a worse answer than telling them plainly.
 *
 * The console is for people who report, administer, or maintain master data.
 * Read-only access to a catalogue is not one of those, so no `*:read` on master
 * data appears below.
 */
/*
 * `masterdata:import` IS NO LONGER A SCREEN OF ITS OWN, and that changes what
 * holding it alone means.
 *
 * `ConsoleApp` picks a landing page with `NAV_ITEMS.find(...)`, and there is no
 * longer a nav item asking for this permission — bulk import is a pair of
 * buttons on each master screen, reached through that screen's own read
 * permission. So an account holding only this one passes `belongsHere` and finds
 * no landing page, and gets the "Tidak ada akses" card.
 *
 * That is the right answer rather than a regression: with no dedicated screen,
 * the permission is a MODIFIER on the master-data screens, and someone who
 * cannot open any of them has nothing to import into. It stays on this list
 * because it is still a permission only a console user would hold, and because
 * removing it would change who can log in — a bigger decision than moving a
 * menu entry. Every role the seed grants it to holds `outlet:read` and
 * `sku:read` besides.
 */
export const CONSOLE_PERMISSIONS: Permission[] = [
  'dashboard:read',
  'report:read',
  'report:export',
  'fraud:read',
  'user:read',
  'outlet:write',
  'sku:write',
  'masterdata:import',
];
